import type { ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@baluu/ui/lib/utils";

/**
 * Renders a booking-form "note" question (`typeOfInput: "noteInput"`) for the
 * customer. The note is authored as Markdown in the dashboard's Tiptap editor,
 * so this supports what that editor produces for a short informational blurb:
 * paragraphs, hard breaks, bold / italic / strikethrough, links, bullet and
 * numbered lists, inline code. Headings are flattened to a bold paragraph.
 * Anything else (tables, images, task-list checkboxes, blockquotes, dividers,
 * code blocks) is unwrapped to its text via `unwrapDisallowed` — a note is not
 * a document.
 *
 * `remark-gfm` is here for autolink literals: notes written before the editor
 * existed are plain text with pasted URLs, and GFM turns those into links
 * without anyone re-editing the listing. It also gives `~~strikethrough~~`
 * and bare email addresses (mailto).
 *
 * Links open in a new tab: the checkout runs inside an overlay on the school's
 * site or the widget, and navigating away would lose the booking in progress.
 * react-markdown's default `urlTransform` drops `javascript:` and other unsafe
 * schemes, and it renders a React tree rather than HTML, so no sanitiser is
 * needed here.
 */
const ALLOWED_ELEMENTS = [
  "p",
  "br",
  "strong",
  "em",
  "del",
  "a",
  "ul",
  "ol",
  "li",
  "code",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
];

function HeadingAsBoldParagraph({ children }: { children?: ReactNode }) {
  return <p className="font-semibold">{children}</p>;
}

const components: Components = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary underline underline-offset-4 hover:opacity-80"
    >
      {children}
    </a>
  ),
  // The widget's global stylesheet resets list markers, so set them explicitly
  // (the same reason `MarkdownRender` in apps/widget does).
  ul: ({ children }) => <ul className="list-disc pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5">{children}</ol>,
  h1: HeadingAsBoldParagraph,
  h2: HeadingAsBoldParagraph,
  h3: HeadingAsBoldParagraph,
  h4: HeadingAsBoldParagraph,
  h5: HeadingAsBoldParagraph,
  h6: HeadingAsBoldParagraph,
};

interface NoteMarkdownProps {
  children: string;
  className?: string;
}

export function NoteMarkdown({ children, className }: NoteMarkdownProps) {
  return (
    <div className={cn("space-y-2 break-words", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        allowedElements={ALLOWED_ELEMENTS}
        unwrapDisallowed
        components={components}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
